import { Card, Metadata } from "@dropbox/dash-component-library";
import { Truncate } from "@dropbox/dig-components/truncate";
import { Text } from "@dropbox/dig-components/typography";
import type {
  SourceRowConfig,
  SourceRowParts,
} from "@mirage/mosaics/Chat/components/compose-sources/ComposeSourceRow";
import {
  getSourceRowParts,
  removeUnderscorePrefix,
} from "@mirage/mosaics/Chat/components/compose-sources/ComposeSourceRow";
import { openURL } from "@mirage/service-platform-actions";
import { getSourceUUID } from "@mirage/shared/compose/compose-session";
import i18n from "@mirage/translations";
import classNames from "classnames";
import { memo, useEffect, useLayoutEffect, useRef, useState } from "react";
import styles from "./AnswerCitationCard.module.css";

import type { OverlayProps } from "@dropbox/dig-components/overlay";
import { Overlay } from "@dropbox/dig-components/overlay";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import type { PAPEvent } from "@mirage/analytics/events/base/event";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import { PAP_Click_DashChatAction } from "@mirage/analytics/events/types/click_dash_chat_action";
import { PAP_Click_SourceFile } from "@mirage/analytics/events/types/click_source_file";
import type {
  ChatConversationMessage,
  ChatConversationMessageMessage,
  ChatSource,
} from "@mirage/mosaics/Chat/types";
import { getCitationAndContextSources } from "@mirage/mosaics/Chat/utils/messageSources";
import useSearchQueryId from "@mirage/shared/hooks/useSearchQueryId";
import Sentry from "@mirage/shared/sentry";
import { useDelayedHover } from "../ConversationAssistant/hooks/useDelayedHover";

const DEFAULT_NUM_CARDS = 4;

const DEFAULT_CITATION_CARD_CONFIG: SourceRowConfig = {
  time: {
    timeAgo: {
      abbreviateTime: true,
      abbreviateDate: true,
    },
    hidePrefix: true,
  },
  icon: {
    hasBackground: false,
    size: "small",
  },
  subtitle: {
    hideServiceName: true,
    fallbackToServiceName: true,
  },
};

interface AnswerCitationCardsProps {
  messages: ChatConversationMessage[];
  conversationId?: string;
  searchSessionId?: string;
  assistRequestId?: string;
}
export const AnswerCitationCards = memo(
  ({
    messages,
    conversationId,
    searchSessionId,
    assistRequestId,
  }: AnswerCitationCardsProps) => {
    const firstResponseMessage = messages.find(
      (message): message is ChatConversationMessageMessage =>
        message.type === "message" && message.role === "assistant",
    );
    const sourcesArray = firstResponseMessage
      ? getCitationAndContextSources(firstResponseMessage)
      : [];
    const [showAll, setShowAll] = useState(false);

    // show N-1 sources + "show more" if there are more than N
    const sourcesToShow =
      !showAll && sourcesArray.length > DEFAULT_NUM_CARDS
        ? sourcesArray.slice(0, DEFAULT_NUM_CARDS - 1)
        : sourcesArray;

    if (sourcesArray.length === 0) {
      return null;
    }
    return (
      <>
        {sourcesToShow.map((source) => (
          <AnswerCitationCard
            key={getSourceUUID(source)}
            source={source}
            conversationId={conversationId}
            searchSessionId={searchSessionId}
            assistRequestId={assistRequestId}
          />
        ))}
        {sourcesToShow.length < sourcesArray.length && (
          <AnswerCitationShowMoreCard
            moreSources={sourcesArray.slice(sourcesToShow.length)}
            onClick={() => setShowAll(true)}
            conversationId={conversationId}
            searchSessionId={searchSessionId}
            assistRequestId={assistRequestId}
          />
        )}
      </>
    );
  },
);
AnswerCitationCards.displayName = "AnswerCitationCards";

interface AnswerCitationCardProps {
  source: ChatSource;
  config?: SourceRowConfig;
  conversationId?: string;
  searchSessionId?: string;
  logComposeEvent?: (
    event: PAPEvent,
    overrides?: { actionSurfaceComponent?: ActionSurfaceComponent },
  ) => void;
  assistRequestId?: string;
}
export const AnswerCitationCard = memo(
  ({
    source,
    config = DEFAULT_CITATION_CARD_CONFIG,
    conversationId,
    searchSessionId,
    logComposeEvent,
    assistRequestId,
  }: AnswerCitationCardProps) => {
    const sourceRowParts = getSourceRowParts(source, config);
    const { searchQueryUuid } = useSearchQueryId();
    const { reportPapEvent } = useMirageAnalyticsContext();
    const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
      e.preventDefault();
      if (sourceRowParts?.mirageResult?.url) {
        openURL(sourceRowParts.mirageResult.url);
        if (logComposeEvent) {
          logComposeEvent(
            PAP_Click_SourceFile({
              featureLine: "assist",
              actionType: "search_result",
              actionSurfaceComponent: "compose_chat_pane",
              conversationId,
              searchSessionId,
              searchQueryUuid,
              assistRequestId,
              resultUuid: sourceRowParts.mirageResult.uuid,
            }),
          );
        } else {
          reportPapEvent(
            PAP_Click_SourceFile({
              featureLine: "assist",
              actionType: "search_result",
              actionSurfaceComponent: "compose_chat_pane",
              conversationId,
              searchSessionId,
              searchQueryUuid,
              assistRequestId,
              resultUuid: sourceRowParts.mirageResult.uuid,
            }),
          );
        }
      }
    };

    useEffect(() => {
      // Send a message to Sentry if the subtitle is empty
      if (sourceRowParts && !sourceRowParts.subtitle) {
        Sentry.withScope((scope) => {
          scope.setTag("sourceRowPartsUuid", sourceRowParts.mirageResult.uuid);
          scope.setTag("sourceRowPartsTitle", sourceRowParts.titleString);
          scope.setContext(`AnswerCitationCard error context`, {
            sourceRowPartsUuid: sourceRowParts.mirageResult.uuid,
            sourceRowPartsTitle: sourceRowParts.titleString,
          });
          Sentry.captureMessage(
            `AnswerCitationCard subtitle is empty`,
            "info",
            {},
            scope,
          );
        });
      }
    }, [sourceRowParts]);

    if (!sourceRowParts) {
      return null;
    }
    return (
      <Card
        variant="outline"
        isLink
        className={classNames(styles.AnswerCitationCard, {
          [styles.AnswerCitationCardNoBorder]: config?.removeBorder,
        })}
        style={{
          padding: "var(--dig-spacing__micro__medium) 4px", // Explicitly apply padding to override parent padding
        }}
      >
        <Card.Contents breakout="large" paddingBottom={"4"}>
          <Card.OverlayLink
            onClick={handleClick}
            href={sourceRowParts.mirageResult.url ?? "#"}
          >
            <Text
              size="medium"
              className={
                config?.dynamicTitleHeight
                  ? undefined
                  : styles.AnswerCitationCardTitle
              }
            >
              <Truncate lines={2}>
                {removeUnderscorePrefix(sourceRowParts.titleString)}
              </Truncate>
            </Text>
          </Card.OverlayLink>
        </Card.Contents>
        <Card.Contents breakout="large">
          <Metadata
            size="small"
            withDividers="blank"
            className={styles.AnswerCitationCardMetadata}
          >
            <Metadata.Item>{sourceRowParts.icon}</Metadata.Item>
            <Metadata.Item>
              {sourceRowParts.subtitle ||
                "sourceRowParts.mirageResult.fileTypeInfo"}
            </Metadata.Item>
          </Metadata>
        </Card.Contents>
      </Card>
    );
  },
);
AnswerCitationCard.displayName = "AnswerCitationCard";

interface AnswerCitationShowMoreCardProps {
  moreSources: ChatSource[];
  onClick: () => void;
  conversationId?: string;
  searchSessionId?: string;
  assistRequestId?: string;
}
export const AnswerCitationShowMoreCard = memo(
  ({
    moreSources,
    onClick,
    conversationId,
    searchSessionId,
    assistRequestId,
  }: AnswerCitationShowMoreCardProps) => {
    const sourcesRowParts: SourceRowParts[] = [];
    for (const source of moreSources) {
      const sourceRowParts = getSourceRowParts(source, {
        icon: {
          hasBackground: false,
          size: "small",
        },
      });
      if (sourceRowParts) {
        sourcesRowParts.push(sourceRowParts);
      }
    }
    return (
      <AnswerCitationCardsInOverlay
        placement={"bottom-end"}
        sources={moreSources}
        refComponent={
          <Card
            variant="outline"
            isLink
            className={styles.AnswerCitationCard}
            style={{
              padding: "var(--dig-spacing__micro__medium) 4px", // Explicitly apply padding to override parent padding
            }}
          >
            <Card.Contents breakout="large">
              <Card.OverlayLink onClick={onClick} href={"#"}>
                <Text
                  size="medium"
                  className={styles.AnswerCitationShowMoreCardTitle}
                >
                  {i18n.t("answers_see_more_citations", {
                    count: moreSources.length,
                  })}
                </Text>
              </Card.OverlayLink>
            </Card.Contents>
            <Card.Contents breakout="large">
              <Metadata
                size="small"
                withDividers="blank"
                className={styles.AnswerCitationCardMetadata}
              >
                {Array.from(
                  new Map(
                    sourcesRowParts
                      .slice(0, 5)
                      .map((item) => [
                        item.mirageResult.connectorInfo.connectorId,
                        item,
                      ]),
                  ).values(),
                ).map((sourcesRowPart, i) => (
                  <Metadata.Item key={i}>{sourcesRowPart.icon}</Metadata.Item>
                ))}
              </Metadata>
            </Card.Contents>
          </Card>
        }
        conversationId={conversationId}
        searchSessionId={searchSessionId}
        assistRequestId={assistRequestId}
      />
    );
  },
);
AnswerCitationShowMoreCard.displayName = "AnswerCitationShowMoreCard";

interface AnswerCitationCardsInOverlayProps {
  placement: OverlayProps["placement"];
  sources: ChatSource[];
  refComponent: React.ReactNode;
  conversationId?: string;
  searchSessionId?: string;
  logComposeEvent?: (
    event: PAPEvent,
    overrides?: { actionSurfaceComponent?: ActionSurfaceComponent },
  ) => void;
  assistRequestId?: string;
}
export const AnswerCitationCardsInOverlay = memo(
  ({
    placement,
    sources,
    refComponent,
    conversationId,
    searchSessionId,
    logComposeEvent,
    assistRequestId,
  }: AnswerCitationCardsInOverlayProps) => {
    const anchorRef = useRef<HTMLDivElement>(null);
    const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);

    const {
      isHovered,
      handlers: { onMouseEnter, onMouseLeave },
    } = useDelayedHover({
      onHoverStart: () => {
        if (logComposeEvent) {
          logComposeEvent(
            PAP_Click_DashChatAction({
              featureLine: "assist",
              dashChatAction: "hover_sources",
              conversationId,
            }),
          );
        }
      },
    });

    useLayoutEffect(() => {
      if (anchorRef.current) {
        const anchorRect = anchorRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const availableSpaceBelow = viewportHeight - anchorRect.bottom;
        const availableSpaceAbove = anchorRect.top;

        // Set max height based on available space
        setMaxHeight(
          Math.max(availableSpaceBelow, availableSpaceAbove) - 30, // Add padding
        );
      }
    }, [isHovered]);

    return (
      <div
        className={styles.AnswerCitationCardsInOverlayContainer}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div ref={anchorRef}>{refComponent}</div>
        {isHovered && (
          <Overlay
            anchorRef={anchorRef}
            placement={placement}
            auto={true}
            className={styles.AnswerCitationCardsInOverlayContent}
            positioningStrategy="fixed"
            style={{ maxHeight: `${maxHeight}px` }}
          >
            <div onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
              {sources.map((source, i) => (
                <div
                  className={styles.AnswerCitationCardsInOverlayCard}
                  key={i}
                >
                  {i == 0 ? undefined : (
                    <div
                      className={styles.AnswerCitationCardsInOverlayCardDivider}
                    />
                  )}
                  <AnswerCitationCard
                    source={source}
                    conversationId={conversationId}
                    searchSessionId={searchSessionId}
                    assistRequestId={assistRequestId}
                    config={{
                      time: {
                        timeAgo: {
                          abbreviateTime: true,
                          abbreviateDate: false,
                        },
                      },
                      icon: {
                        hasBackground: false,
                        size: "small",
                      },
                      subtitle: {
                        hideServiceName: true,
                        hideSizeOrMtime: false,
                        fallbackToServiceName: true,
                      },
                      dynamicTitleHeight: true,
                      removeBorder: true,
                    }}
                    logComposeEvent={logComposeEvent}
                  />
                </div>
              ))}
            </div>
          </Overlay>
        )}
      </div>
    );
  },
);
AnswerCitationCardsInOverlay.displayName = "AnswerCitationCardsInOverlay";
